import { InboxView } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { ListThreadsIO } from '../../types';
import { convertSMSStatusToPossibleThreadReadStatuses } from './convert-thread-to-applicable-thread-read-statuses';

type ThreadCouldBeInListThreadsResponseArgs = {
  thread: Thread;
  listThreadsRequest: ListThreadsIO['input'];
};

export const threadCouldBeInListThreadsResponse = ({
  thread,
  listThreadsRequest,
}: ThreadCouldBeInListThreadsResponseArgs): boolean => {
  // locationIds
  if (listThreadsRequest.locationIds.length && !listThreadsRequest.locationIds.includes(thread.locationId)) {
    return false;
  }

  // filter.departmentIds
  if (
    listThreadsRequest.filter?.departmentIds?.length &&
    !listThreadsRequest.filter.departmentIds.includes(thread.departmentId)
  ) {
    return false;
  }

  // filter.personPhones
  if (
    listThreadsRequest.filter?.personPhones?.length &&
    thread.messages.length &&
    !listThreadsRequest.filter.personPhones.some(
      (personPhone) => formatPhoneNumberE164(personPhone) === formatPhoneNumberE164(thread.messages[0]!.personPhone)
    )
  ) {
    return false;
  }

  // filter.locationPhones
  if (
    listThreadsRequest.filter?.locationPhones?.length &&
    !listThreadsRequest.filter.locationPhones.some(
      (locationPhone) =>
        // TODO: remove accessing locationPhone from thread.messages[0] once the BE is returning it on the thread
        formatPhoneNumberE164(locationPhone) ===
        formatPhoneNumberE164(thread.locationPhone || thread.messages[0]?.locationPhone || '')
    )
  ) {
    return false;
  }

  // filter.statuses
  const statusFilters = listThreadsRequest.filter?.statuses ?? [];
  const hasStatusFilters = !!statusFilters.length;
  const applicableThreadReadStatuses = convertSMSStatusToPossibleThreadReadStatuses(thread);
  if (
    hasStatusFilters &&
    applicableThreadReadStatuses?.length &&
    !applicableThreadReadStatuses.some((threadReadStatus) =>
      listThreadsRequest.filter?.statuses?.includes(threadReadStatus)
    )
  ) {
    return false;
  }

  // filter.inboxView
  const inboxView = listThreadsRequest.filter?.inboxView ?? InboxView.UNSPECIFIED;
  if (inboxView === InboxView.ACTIONABLE && thread.actionable === false) {
    return false;
  } else if (inboxView === InboxView.BLOCKED && thread.isBlocked === false) {
    return false;
  } else if (inboxView !== InboxView.BLOCKED && thread.isBlocked === true) {
    return false;
  } else if (inboxView === InboxView.ARCHIVED && !thread.isArchived) {
    return false;
  } else if (inboxView !== InboxView.ARCHIVED && thread.isArchived) {
    return false;
  }

  // filter.excludeAutomatedLastMessage is not supported for thread updates. SMS updaters should be used instead.

  // filter.personIds
  if (
    listThreadsRequest.filter?.personIds?.length &&
    !listThreadsRequest.filter.personIds.includes(thread.person?.personId ?? 'invalid-id') // Always false if personId isn't present
  ) {
    return false;
  }

  // filter.messageTags
  if (
    listThreadsRequest.filter?.messageTags?.length &&
    !listThreadsRequest.filter.messageTags.some((tagId) => thread.uniqueTags?.some((tag) => tag.tagId === tagId))
  ) {
    return false;
  }

  // filter.taskStatuses
  if (listThreadsRequest.filter?.taskStatuses?.length) {
    const threadTasks = thread.tasks || [];
    if (threadTasks.length === 0) {
      return false; // Thread has no tasks, shouldn't be visible when task status filter is applied
    }

    // Check if any of the thread's tasks match the filtered task statuses
    const hasMatchingTaskStatus = threadTasks.some((task) =>
      listThreadsRequest.filter?.taskStatuses?.includes(task.status)
    );

    if (!hasMatchingTaskStatus) {
      return false;
    }
  }

  return true;
};
